'use client';

import {
  CalConfig,
  FacebookConfig,
  FeaturebaseConfig,
  GoogleAnalyticsConfig,
  GoogleTagManagerConfig,
  NestedAgency,
  NestedHome,
  NestedUser,
  ProviderType,
  ServiceformConfig,
  ProductLaneConfig,
  CookieTractorConfig,
  HotjarConfig,
} from '@habity/types';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getProviderSettings, hasProvider } from '@habity/agencies';
import { createClient } from '~/lib/providers/supabase/client';
import { getUserDataQuery } from '~/lib/providers/supabase/users/query';
import { getUserAvatarUrl } from '~/lib/providers/supabase/avatars';
import { useFacebook } from '~/hooks/providers/useFacebook';
import { useServiceform } from '~/hooks/providers/useServiceform';
import { useTracking } from '~/hooks/useTracking';
import { GoogleAnalytics } from '@next/third-parties/google';
import { GoogleTagManager } from '@next/third-parties/google';
import { isDev } from '~/lib/utils';
import Script from 'next/script';
import { useFeaturebase } from '~/hooks/providers/useFeaturebase';
import { useProductLane } from '~/hooks/providers/useProductLane';
import { useCookieTractor } from '~/hooks/providers/useCookieTractor';
import { useHotjar } from '~/hooks/providers/useHotjar';
import { isBrokerOrAdmin } from '~/lib/roles';
import { initCalGlobally } from '~/hooks/providers/useCal';
import { PostHogPageView } from '~/components/tracking/PostHogPageView';

const FEATUREBASE_SECRET =
  process.env.NEXT_PUBLIC_FEATUREBASE_IDENTITY_VERIFICATION_SECRET || null;

async function computeHmacSha256(
  secret: string,
  message: string
): Promise<string> {
  const encoder = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await window.crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(message)
  );
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

type AppContextValue = {
  agency: NestedAgency | null;
  user: NestedUser | null;
  userHash: string | null;
  setUser: ((user: NestedUser) => void) | null;
  isUserLoaded: boolean;
};

type AppProviderProps = React.PropsWithChildren<{
  agency: NestedAgency | null;
  user: NestedUser | null;
  userHash: string | null;
}>;

const defaultAppContext: AppContextValue = {
  agency: null,
  user: null,
  userHash: null,
  setUser: null,
  isUserLoaded: false,
};

export const AppContext = createContext<AppContextValue>(defaultAppContext);

export const AppProvider = ({ children, agency }: AppProviderProps) => {
  const [user, setUser] = useState<NestedUser | null>(null);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [userHash, setUserHash] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const { init: initFacebook } = useFacebook();
  const { init: initFeaturebase } = useFeaturebase();
  const { init: initProductLane } = useProductLane();
  const { init: initTracking } = useTracking();
  const { init: initServiceform } = useServiceform();
  const { init: initCookieTractor } = useCookieTractor();
  const { init: initHotjar } = useHotjar();

  const disablePopups = searchParams?.get('disablePopups') === 'true';

  // Stable callback for UserSetter to inject server-hydrated user
  const handleSetUser = useCallback((u: NestedUser) => {
    setUser(u);
    setIsUserLoaded(true);
  }, []);

  // Client-side fallback: only runs if server hydration didn't provide user data
  useEffect(() => {
    if (isUserLoaded || user) return;

    const fetchUser = async () => {
      try {
        const supabase = createClient();
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        if (!authUser) {
          setIsUserLoaded(true);
          return;
        }

        const { data: userData, error } = await supabase
          .from('users')
          .select(getUserDataQuery())
          .or(`uuid.eq.${authUser.id},email.eq.${authUser.email}`)
          .limit(1)
          .maybeSingle();

        if (error || !userData) {
          setIsUserLoaded(true);
          return;
        }

        // Type the response matching getUserDataQuery() shape
        // Supabase returns a union with GenericStringError, but we've already checked for errors above
        const u = userData as unknown as NestedUser & {
          user_images?: Array<{ id: number; extension?: string }>;
          user_roles?: Array<{ role: string }>;
        };

        // Get avatar URL
        const avatarUrl = u.user_images?.[0]
          ? getUserAvatarUrl(
              supabase,
              'user-images',
              u.id,
              u.user_images[0].extension || ''
            )
          : undefined;

        // Get homes count via sellers table
        const { data: sellerRecords } = await supabase
          .from('sellers')
          .select('home_id')
          .eq('user_id', u.id);

        const uniqueHomeIds = [
          ...new Set(
            sellerRecords?.map((s: { home_id: number }) => s.home_id) || []
          ),
        ];
        const roles = u.user_roles?.map((r) => r.role) || [];

        setUser({
          ...u,
          avatar_url: avatarUrl || u.avatar_url,
          homes: uniqueHomeIds.map((id) => ({
            id: id as number,
          })) as NestedHome[],
          homes_count: uniqueHomeIds.length,
          roles,
        });
        setIsUserLoaded(true);
      } catch (error) {
        console.error('[AppProvider] Failed to fetch user:', error);
        setIsUserLoaded(true);
      }
    };

    fetchUser();
  }, [isUserLoaded, user]);

  const getGoogleId = (provider: ProviderType) => {
    if (!agency) return '';
    const settings = getProviderSettings(agency, provider);
    if (provider === 'google_analytics') {
      const { gaId } = (settings as GoogleAnalyticsConfig) || {};
      return gaId || '';
    } else if (provider === 'google_tag_manager') {
      const { gtmId } = (settings as GoogleTagManagerConfig) || {};
      return gtmId || '';
    }
    return '';
  };

  useEffect(() => {
    // Check if we're in Plasmic editor - skip all provider initialization
    const inPlasmicEditor =
      typeof window !== 'undefined' &&
      window.location.pathname === '/plasmic-host';

    if (!agency || inPlasmicEditor) return;

    // Initialize Cal.com globally (works in all environments)
    initCalGlobally().catch((error) => {
      console.error('[App] Failed to initialize Cal.com:', error);
    });

    if (isDev) return;
    // Place staging/production-only providers below this line

    // Initialize tracking with agency settings and user data
    // The useTracking hook will extract all necessary tracking provider settings
    initTracking(agency, user);

    if (hasProvider(agency, 'facebook')) {
      const facebook = getProviderSettings(
        agency,
        'facebook'
      ) as FacebookConfig;
      initFacebook(facebook);
    }
    if (hasProvider(agency, 'productlane')) {
      const productLane = getProviderSettings(
        agency,
        'productlane'
      ) as ProductLaneConfig;
      initProductLane(productLane);
    }

    if (hasProvider(agency, 'cookie_tractor') && !disablePopups) {
      const cookieTractor = getProviderSettings(
        agency,
        'cookie_tractor'
      ) as CookieTractorConfig;
      initCookieTractor(cookieTractor);
    }

    if (hasProvider(agency, 'hotjar')) {
      const hotjar = getProviderSettings(agency, 'hotjar') as HotjarConfig;
      initHotjar(hotjar);
    }
  }, []);

  // Serviceform: wait for user data so we can gate on broker/admin role
  useEffect(() => {
    if (!agency || !isUserLoaded || isDev) return;
    if (
      hasProvider(agency, 'serviceform') &&
      !isBrokerOrAdmin(user) &&
      !disablePopups
    ) {
      const serviceform = getProviderSettings(
        agency,
        'serviceform'
      ) as ServiceformConfig;
      const { pixelId } = serviceform || {};
      if (pixelId) {
        initServiceform(pixelId);
      }
    }
  }, [isUserLoaded]);

  // Featurebase: First, compute userhash (once user is fetched)
  useEffect(() => {
    if (!user || !FEATUREBASE_SECRET) return;
    computeHmacSha256(FEATUREBASE_SECRET, user.email).then(setUserHash);
  }, [user]);

  // Featurebase: Second, initialize once user and userhash are present (non-dev only)
  useEffect(() => {
    if (isDev) return;
    if (user && userHash && hasProvider(agency, 'featurebase')) {
      const featurebase = getProviderSettings(
        agency,
        'featurebase'
      ) as FeaturebaseConfig;
      initFeaturebase({ ...featurebase, user, userHash });
    }
  }, [user, userHash]);

  return (
    <AppContext.Provider
      value={{ agency, user, userHash, setUser: handleSetUser, isUserLoaded }}
    >
      {children}

      {/* PostHog pageview tracking for App Router navigation */}
      {!isDev && hasProvider(agency, 'posthog') && <PostHogPageView />}

      {!isDev && hasProvider(agency, 'google_analytics') && (
        <GoogleAnalytics gaId={getGoogleId('google_analytics')} />
      )}

      {!isDev && hasProvider(agency, 'google_tag_manager') && (
        <GoogleTagManager gtmId={getGoogleId('google_tag_manager')} />
      )}

      {hasProvider(agency, 'featurebase') && (
        <Script
          src="https://do.featurebase.app/js/sdk.js"
          id="featurebase-sdk"
          strategy="afterInteractive"
        />
      )}

      {hasProvider(agency, 'productlane') && (
        <Script
          src="https://widget.productlane.com/latest.productlane-widget.min.js"
          id="productlane-config"
          async
          defer
          crossOrigin="anonymous"
        />
      )}
    </AppContext.Provider>
  );
};

export default AppProvider;
